import React, { useEffect, useState } from 'react';

import { useThemeContext } from 'providers/ThemeProvider';
import { logger } from 'utils/logger';

interface ThemeFallbackProps {
  children: React.ReactNode;
}

const ThemeFallback: React.FC<ThemeFallbackProps> = ({ children }) => {
  const theme = useThemeContext();
  const [hasThemeError, setHasThemeError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    // Check if the theme is loaded correctly
    if (theme) {
      const hasValidColors = theme.primary && theme.background?.primary && theme.text?.primary;

      if (!hasValidColors) {
        logger.warn('[ThemeFallback] Invalid theme detected:', { data: theme });
        setHasThemeError(true);
      } else {
        setHasThemeError(false);
      }
    }
  }, [theme]);

  // Apply fallback styles on body
  useEffect(() => {
    const applyFallbackStyles = () => {
      document.body.style.backgroundColor = '#0A0A0A';
      document.body.style.color = '#E9E9F0';
      document.body.style.fontFamily =
        'var(--font-sans, Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif)';
      document.body.style.margin = '0';
      document.body.style.padding = '0';
    };

    if (hasThemeError || !theme) {
      applyFallbackStyles();
    } else {
      // Remove fallback styles when theme is ok
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
      document.body.style.fontFamily = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
    }

    return () => {
      // Cleanup on unmount
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
      document.body.style.fontFamily = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
    };
  }, [hasThemeError, theme]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setHasThemeError(false);
    // Force theme reload
    window.location.reload();
  };

  if (!theme) return null;

  if (hasThemeError && retryCount < 3) {
    return (
      <div
        style={{
          backgroundColor: '#0A0A0A',
          color: '#E9E9F0',
          fontFamily:
            'var(--font-sans, Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif)',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h2 style={{ color: '#DBB958', marginBottom: '1rem' }}>Problema ao carregar o tema</h2>
        <p style={{ marginBottom: '2rem', maxWidth: '400px' }}>
          Houve um problema ao carregar as configurações visuais da aplicação. Isso pode afetar a
          aparência dos botões e outros elementos.
        </p>
        <button
          onClick={handleRetry}
          className="rounded-lg"
          style={{
            backgroundColor: '#DBB958',
            color: '#000000',
            border: 'none',
            padding: '12px 24px',
            fontSize: '16px',
            cursor: 'pointer',
            fontFamily:
              'var(--font-sans, Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif)',
          }}
        >
          Tentar novamente
        </button>
        <p style={{ marginTop: '1rem', fontSize: '14px', color: '#CECECE' }}>
          Tentativa {retryCount + 1} de 3
        </p>
      </div>
    );
  }

  if (hasThemeError && retryCount >= 3) {
    return (
      <div
        style={{
          backgroundColor: '#0A0A0A',
          color: '#E9E9F0',
          fontFamily:
            'var(--font-sans, Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif)',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h2 style={{ color: '#FF0000', marginBottom: '1rem' }}>Erro persistente no tema</h2>
        <p style={{ marginBottom: '2rem', maxWidth: '400px' }}>
          Não foi possível carregar o tema após várias tentativas. A aplicação pode não funcionar
          corretamente.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-lg"
          style={{
            backgroundColor: '#FF0000',
            color: '#FFFFFF',
            border: 'none',
            padding: '12px 24px',
            fontSize: '16px',
            cursor: 'pointer',
            fontFamily:
              'var(--font-sans, Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif)',
          }}
        >
          Recarregar página
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

export default ThemeFallback;
